import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { DrawerItems } from '@/components/Drawer/Drawer.styles';
import {
  getCardSystemLogo,
  getPaymentMethodsInfo,
} from '@/components/Payment/PaymentMethods.helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import * as S from '@/components/Payment/PaymentMethods.styles';
import { PaymentMethod } from '@/api/types/payments.types';
import { GetUserResponse, UserCard } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { useAuthStore } from '@/store/useAuthStore';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { useMobileDetect } from '@/hooks/useMobileDetect';
import { useServerDataContext } from '@/contexts';

type Props = {
  payment: PaymentMethod;
  setPayment: React.Dispatch<React.SetStateAction<PaymentMethod>>;
  setCardId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  isAddBalanceOpen?: boolean;
  isPromotionDrawerOpen?: boolean;
};

export const PaymentMethods = ({
  payment,
  setPayment,
  setCardId,
  isAddBalanceOpen,
  isPromotionDrawerOpen,
}: Props) => {
  const { t } = useTranslation();
  const [isCardsBSOpen, cardsBSToggle] = useToggleOpenState();
  const [selectedCard, setSelectedCard] = useState<UserCard | null>(null);
  const [isAuthenticated] = useAuthStore(state => [state.isAuthenticated]);
  const { isWebView } = useBridgeState();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const { paymentMethods, isAppleGooglePayEnabled } = adData || {};
  const { isAndroid, isIos } = useMobileDetect();

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER, {
    onCompleted: data => {
      const cards = data.currentUser?.cards?.nodes;
      if (cards?.length) {
        setSelectedCard(cards[0]);
        setCardId?.(cards[0].id);
      }
    },
  });

  const isB2B = useMemo(
    () => isAuthenticated && userInfo?.currentUser?.isShop,
    [isAuthenticated, userInfo]
  );

  function selectCard(card: UserCard) {
    setSelectedCard(card);
    setCardId?.(card.id);
    cardsBSToggle(false);
  }

  function onToggleMyCards(e: React.MouseEvent) {
    e.stopPropagation();
    cardsBSToggle();
  }

  useEffect(() => {
    if (isAddBalanceOpen) {
      setPayment(
        isB2B || !selectedCard ? PaymentMethod.Card : PaymentMethod.My_card
      );
    } else if (isB2B) {
      setPayment(PaymentMethod.Wallet);
    }
  }, [isAddBalanceOpen, isB2B, isPromotionDrawerOpen]);

  return (
    <>
      <DrawerItems $isInTheMiddle>
        <S.DrawerItemTitle $variant="3">
          {t('pages.ad.payment_method')}
        </S.DrawerItemTitle>

        <S.List>
          {selectedCard && (
            <S.ListItem
              dataTestId={PaymentMethod.My_card}
              onChangeHook={() => setPayment(PaymentMethod.My_card)}
              value={PaymentMethod.My_card === payment}
              label={selectedCard.name}
              labelIcon={
                <IconBox
                  $filename={`${getCardSystemLogo(selectedCard)}.png`}
                  $width={45}
                  $height={30}
                  $iconWidth={34}
                />
              }
              labelPosition="right"
              action={
                <S.CheckboxAction
                  $variant="1"
                  onClick={onToggleMyCards}
                  data-testid={AdPageTestIds.myCards.triggerButton}
                >
                  {t('pages.cabinet.my_cards')}
                </S.CheckboxAction>
              }
              appearance="radio"
              size="md"
              color="secondary"
            />
          )}

          {paymentMethods &&
            getPaymentMethodsInfo(
              t,
              paymentMethods,
              userInfo?.currentUser?.walletBalance
            ).map(p => {
              const isTerminal = p.value === PaymentMethod.Terminal;
              const isWallet = p.value === PaymentMethod.Wallet;
              const isBankCard = p.value === PaymentMethod.Card;
              const isPortmanatOnWebView =
                isWebView && p.value === PaymentMethod.Portmanat;

              if (
                ((isAddBalanceOpen || !isAuthenticated) && isWallet) ||
                isPortmanatOnWebView
              ) {
                return null;
              }

              return (
                <S.ListItem
                  dataTestId={p.value}
                  key={p.value}
                  onChangeHook={() => setPayment(p.value)}
                  value={p.value === payment}
                  label={p.label}
                  {...(isB2B &&
                    isWallet && {
                      $order: -1,
                    })}
                  {...(isTerminal && {
                    labelIcon: (
                      <IconBox
                        $filename="payment-terminal.svg"
                        $width={15}
                        $height={30}
                        $isMirroredX
                      />
                    ),
                  })}
                  {...(isBankCard &&
                    isAppleGooglePayEnabled &&
                    (isIos || isAndroid) && {
                      labelIcon: (
                        <IconBox
                          data-testid={
                            isIos
                              ? AdPageTestIds.paymentProviderIcons.applePayIcon
                              : AdPageTestIds.paymentProviderIcons.googlePayIcon
                          }
                          $filename={isIos ? 'apple-pay.svg' : 'google-pay.svg'}
                          $width={48}
                          $height={30}
                        />
                      ),
                    })}
                  labelPosition="right"
                  appearance="radio"
                  size="md"
                  color="secondary"
                />
              );
            })}
        </S.List>
      </DrawerItems>

      <BottomSheet
        dataTestId={AdPageTestIds.myCards.bottomSheet.self}
        isOpen={isCardsBSOpen}
        close={cardsBSToggle}
        title={t('pages.cabinet.my_cards')}
      >
        {userInfo?.currentUser?.cards?.nodes.map(card => (
          <S.CardBox
            data-testid={AdPageTestIds.myCards.bottomSheet.card}
            key={card.id}
            $isActive={card.id === selectedCard?.id}
            onClick={() => selectCard(card)}
          >
            <S.CardName $variant="1">{card.name}</S.CardName>

            <IconBox
              $filename={`${getCardSystemLogo(card)}.png`}
              $width={45}
              $height={30}
              $iconWidth={34}
            />
          </S.CardBox>
        ))}
      </BottomSheet>
    </>
  );
};
